import type { Context } from '@/@types/ssr';
import type { AuthConsents, LoginProvider, SuccessType } from '../types';

import { AccountModes, AUTH_CONSENTS_KEY } from '../types';
import { useAuthTracking } from '../tracking';

import { LoginMessageModal } from '@/lazy-load-components';

import { toastError } from '@/helpers/vfm/toast-helper';
import { currentUser } from '@/helpers/user-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { getVm } from '@/helpers/vm-helper';

import { useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';

import { onMounted, getCurrentInstance } from 'vue';
import Cookies from 'js-cookie';

const NOT_REGISTERED = 'NOT_REGISTERED';

export function useLoginFlow() {
	const { tc } = useI18n();
	const { trackAuth } = useAuthTracking();
	const {
		accessToken,
		isLoggedIn,
		signInInternally,
		registerInternally,
		info,
		jwLoginId,
		logout,
		consents,
		saveSettings,
		deleteAccountPermanently,
		setLoginProgress,
	} = useUserStore();

	async function authenticateUser() {
		try {
			/* VERIFY REGISTRATION */
			// If this succeeds, then it means the user is registered internally
			// (meaning the operation is a login operation)
			const user = await currentUser();

			if (!user) return;
			const tokenResult = await user.getIdTokenResult();

			if (tokenResult.claims.registration_error) {
				throw new Error(`registration error: ${tokenResult.claims.registration_error}`);
			}

			if (!tokenResult.claims.jw_login_id) {
				throw new Error('not yet registered internally');
			}

			/* LOGIN OPERATION */
			try {
				const authConsents: AuthConsents | null = sessionStorage.getItem(AUTH_CONSENTS_KEY)
					? JSON.parse(sessionStorage.getItem(AUTH_CONSENTS_KEY)!)
					: null;
				await signInInternally();

				trackAuth('login', {
					spec: {
						property: authConsents?.location
							? `${info.value.loginProvider}_${authConsents.location}`
							: info.value.loginProvider,
						label: jwLoginId.value,
					},
				});

				sessionStorage.removeItem(AUTH_CONSENTS_KEY);
				openSuccessModal('logged-in');
			} catch (err) {
				// If we can't sign in with Firebase SDK then we logout the user (jw-app state)
				await logout();

				trackAuth('loginError', {
					spec: {
						property: info.value as LoginProvider,
						label: (err as Error).message.toString(),
					},
				});

				captureMessageForSentry(
					'[Authentication]: Login failed',
					{ error: err as Error, where: '[user-helper.ts]: authenticateUser()' },
					'error',
					{ AuthFail: info.value.loginProvider }
				);

				openSuccessModal('login-failed');
			}
		} catch {
			// sign up instead
			throw new Error('User is not yet registered with our internal backend.', { cause: NOT_REGISTERED });
		}
	}

	async function signUpUser() {
		try {
			await registerInternally();

			const authConsents: AuthConsents | null = sessionStorage.getItem(AUTH_CONSENTS_KEY)
				? JSON.parse(sessionStorage.getItem(AUTH_CONSENTS_KEY)!)
				: null;

			if (authConsents) {
				// If the user creates account with the SIGN_IN modal,
				// we want to save the user consents as true and assume they gave consent
				await saveSettings({
					jw_consents: {
						...consents.value,
						emails: authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedEmailAgreement : true,
						terms_and_conditions:
							authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedPrivacyPolicy : true,
					},
				});

				trackAuth('dataProtectionSubmitted');

				const acceptEmails =
					authConsents.acceptedEmailAgreement == true || authConsents.mode === AccountModes.SIGN_IN ? 1 : 0;
				trackAuth('interestEmailsSubmitted', { spec: { value: acceptEmails } });

				trackAuth('signUp', {
					spec: {
						property: authConsents?.location
							? `${info.value.loginProvider}_${authConsents.location}`
							: info.value.loginProvider,
						label: jwLoginId.value,
					},
				});

				sessionStorage.removeItem(AUTH_CONSENTS_KEY);
			}

			openSuccessModal('logged-in');
		} catch (err) {
			// If the registration in consumer-api fails,
			// we just want to delete the user in firebase and then show a failure message.
			await deleteAccountPermanently();

			openSuccessModal('sign-up-failed');
			trackAuth('signUpError', {
				spec: {
					property: info.value.loginProvider as LoginProvider,
					label: (err as Error).message,
				},
			});
		}
	}

	onMounted(async () => {
		const user = await currentUser();

		if (!isLoggedIn.value && user) {
			// Firebase user exists but our app state says logged out
			// Need to determine if this is a fresh login or a stale Firebase session
			const storeHasAuth = !!(jwLoginId.value && accessToken.value);

			if (!storeHasAuth) {
				// Check if access_token cookie exists first
				// If cookie exists, trust it (cross-subdomain navigation case)
				const accessTokenCookie = Cookies.get('access_token');

				if (!accessTokenCookie) {
					// No cookie - could be fresh login or stale session
					// Check sessionStorage to distinguish between the two
					const authConsentsStr = sessionStorage.getItem(AUTH_CONSENTS_KEY);

					if (!authConsentsStr) {
						// No cookie AND no sessionStorage = stale Firebase session
						// This happens when user logged out on another subdomain
						await logout();
						setLoginProgress(false);
						return;
					}
					// sessionStorage has consents = fresh login, proceed with auth
				}
				// Cookie exists = cross-subdomain navigation with active session, proceed with auth
			}

			try {
				await authenticateUser();
			} catch (error) {
				if ((error as Error).cause === NOT_REGISTERED) await signUpUser();
				else throw new Error('Unable to authenticate user.');
			}
		}

		const { proxy: instance } = getCurrentInstance() ?? { proxy: getVm() };
		const context = instance.$context as Context;

		if (context?.showRefreshTokenError) {
			context.showRefreshTokenError = false;
			toastError({ message: tc('WEBAPP_REFRESH_TOKEN_ERROR'), duration: 4000 });
		}
	});
}

function openSuccessModal(successType: SuccessType) {
	try {
		ModalHelper.openModal(
			LoginMessageModal,
			{ successType, closeDelay: 2000 },
			{
				id: 'login-message-modal',
			}
		);
	} catch (error: any) {
		captureMessageForSentry(
			'[Opening Login Message Modal]:failed',
			{ error, where: '[user-helper.ts]: openSuccessModal()' },
			'error'
		);
	}
}
